/**
 * Willtec Complementary Products Component ("Compre Junto")
 * Handles native Shopify Product Recommendations (intent=complementary),
 * dynamic main product variant synchronization, interactive item selection,
 * real-time total calculation without bundle discounts, and single multi-item
 * cart submission with minicart refresh.
 */
(() => {
  if (customElements.get('willtec-complementary-products')) return;

  class WilltecComplementaryProducts extends HTMLElement {
  constructor() {
    super();
    this.isSubmitting = false;
  }

  connectedCallback() {
    const box = this.querySelector('[data-complementary-box]');
    if (box) {
      this.init();
      return;
    }

    const url = this.dataset.url;
    if (!url) return;

    const loadRecommendations = (fetchUrl) => {
      fetch(fetchUrl)
        .then((res) => {
          if (!res.ok) throw new Error('Failed to fetch complementary recommendations');
          return res.text();
        })
        .then((html) => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const compEl = doc.querySelector('willtec-complementary-products');

          if (compEl) {
            const contentBox = compEl.querySelector('[data-complementary-box]');
            if (contentBox) {
              this.innerHTML = compEl.innerHTML;
              this.style.display = '';
              this.init();
              return;
            }
          }

          // Fallback: se não houver produtos complementares manuais cadastrados na loja, busca related
          if (fetchUrl.includes('intent=complementary')) {
            const fallbackUrl = fetchUrl.replace('intent=complementary', 'intent=related');
            loadRecommendations(fallbackUrl);
            return;
          }

          this.style.display = 'none';
        })
        .catch(() => {
          if (fetchUrl.includes('intent=complementary')) {
            const fallbackUrl = fetchUrl.replace('intent=complementary', 'intent=related');
            loadRecommendations(fallbackUrl);
            return;
          }
          this.style.display = 'none';
        });
    };

    loadRecommendations(url);
  }

  init() {
    this.mainItem = this.querySelector('[data-complementary-item="main"]');
    this.items = this.querySelectorAll('[data-complementary-item]');
    this.checkboxes = this.querySelectorAll('[data-complementary-checkbox]');
    this.submitBtn = this.querySelector('[data-complementary-submit]');
    this.countDisplay = this.querySelector('[data-complementary-count]');
    this.totalDisplay = this.querySelector('[data-complementary-total]');
    this.errorDisplay = this.querySelector('[data-complementary-error]');

    if (this.submitBtn) {
      this.originalBtnText = this.submitBtn.innerHTML;
      this.submitBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleSubmit();
      });
    }

    this.checkboxes.forEach((cb) => {
      cb.addEventListener('change', () => {
        this.syncImageVisibility();
        this.recalculate();
      });
    });

    // Listen to PDP variant change event
    this.handleVariantChange = (e) => this.onVariantChange(e.detail);
    document.addEventListener('willtec:variant:change', this.handleVariantChange);

    this.syncImageVisibility();
    this.recalculate();
  }

  disconnectedCallback() {
    if (this.handleVariantChange) {
      document.removeEventListener('willtec:variant:change', this.handleVariantChange);
    }
  }

  onVariantChange(detail) {
    if (!this.mainItem || !detail) return;

    if (detail.variantId) {
      this.mainItem.dataset.variantId = detail.variantId;
    }

    if (typeof detail.price === 'number') {
      this.mainItem.dataset.price = detail.price;
    }

    // Update main product image if variant has a distinct image
    if (detail.featuredMedia && (detail.featuredMedia.src || detail.featuredMedia.preview_image)) {
      const mainImg = this.querySelector('[data-complementary-image="main"]');
      if (mainImg) {
        const newSrc = detail.featuredMedia.src || (detail.featuredMedia.preview_image && detail.featuredMedia.preview_image.src);
        if (newSrc) mainImg.src = newSrc;
      }
    }

    this.recalculate();
  }

  syncImageVisibility() {
    // Dim or highlight images based on checkbox state
    this.items.forEach((item) => {
      const id = item.dataset.complementaryItem;
      const cb = item.querySelector('[data-complementary-checkbox]');
      const imgWrapper = this.querySelector(`[data-complementary-thumb="${id}"]`);
      if (imgWrapper && cb) {
        if (cb.checked) {
          imgWrapper.classList.remove('opacity-40');
        } else {
          imgWrapper.classList.add('opacity-40');
        }
      }
    });
  }

  recalculate() {
    let totalCents = 0;
    let checkedCount = 0;

    this.items.forEach((item) => {
      const cb = item.querySelector('[data-complementary-checkbox]');
      if (cb && cb.checked) {
        const price = parseInt(item.dataset.price, 10) || 0;
        totalCents += price;
        checkedCount++;
      }
    });

    if (this.countDisplay) {
      this.countDisplay.textContent = checkedCount;
    }

    if (this.totalDisplay) {
      this.totalDisplay.textContent = this.formatMoney(totalCents);
    }

    // If only the main product is selected, disable submit button
    if (this.submitBtn) {
      if (checkedCount <= 1) {
        this.submitBtn.disabled = true;
      } else {
        this.submitBtn.disabled = false;
      }
    }
  }

  formatMoney(cents) {
    return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  async handleSubmit() {
    if (this.isSubmitting) return;

    if (this.errorDisplay) {
      this.errorDisplay.classList.add('hidden');
      this.errorDisplay.textContent = '';
    }

    const checkedItems = [];
    this.items.forEach((item) => {
      const cb = item.querySelector('[data-complementary-checkbox]');
      if (cb && cb.checked) {
        const variantId = parseInt(item.dataset.variantId, 10);
        if (variantId) {
          checkedItems.push({
            id: variantId,
            quantity: 1
          });
        }
      }
    });

    if (checkedItems.length <= 1) {
      if (this.errorDisplay) {
        this.errorDisplay.textContent = 'Selecione ao menos um produto complementar para comprar junto.';
        this.errorDisplay.classList.remove('hidden');
      }
      return;
    }

    this.isSubmitting = true;
    if (this.submitBtn) {
      this.submitBtn.disabled = true;
      this.submitBtn.setAttribute('aria-busy', 'true');
      this.submitBtn.innerHTML = '<span class="animate-pulse">Adicionando...</span>';
    }

    const rootUrl = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';

    try {
      const response = await fetch(`${rootUrl}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: checkedItems,
          sections: 'willtec-cart-drawer,header',
          sections_url: window.location.pathname
        })
      });

      const data = await response.json();

      if (response.ok) {
        if (data.sections) {
          const drawerEl = document.getElementById('WilltecCartDrawer');
          if (drawerEl && data.sections['willtec-cart-drawer']) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(data.sections['willtec-cart-drawer'], 'text/html');
            const newDrawerContent = doc.getElementById('WilltecCartDrawer');
            if (newDrawerContent) {
              drawerEl.innerHTML = newDrawerContent.innerHTML;
              if (typeof drawerEl.bindEvents === 'function') {
                drawerEl.bindEvents();
              }
            }
          }

          if (data.sections['header']) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(data.sections['header'], 'text/html');
            const newHeaderCounts = doc.querySelectorAll('.willtec-cart-count');
            document.querySelectorAll('.willtec-cart-count').forEach((badge, idx) => {
              if (newHeaderCounts[idx]) badge.innerHTML = newHeaderCounts[idx].innerHTML;
            });
          }
        }

        document.dispatchEvent(new CustomEvent('cart:refresh'));
        document.dispatchEvent(new CustomEvent('cart:open'));
        document.dispatchEvent(new CustomEvent('willtec:cart:open'));
      } else {
        const errorMsg = data.description || data.message || 'Erro ao adicionar produtos ao carrinho.';
        if (this.errorDisplay) {
          this.errorDisplay.textContent = errorMsg;
          this.errorDisplay.classList.remove('hidden');
        }
      }
    } catch (err) {
      if (this.errorDisplay) {
        this.errorDisplay.textContent = 'Ocorreu um erro ao processar seu pedido. Tente novamente.';
        this.errorDisplay.classList.remove('hidden');
      }
    } finally {
      this.isSubmitting = false;
      if (this.submitBtn) {
        this.submitBtn.disabled = false;
        this.submitBtn.removeAttribute('aria-busy');
        this.submitBtn.innerHTML = this.originalBtnText;
      }
    }
  }
}

  if (!customElements.get('willtec-complementary-products')) {
    customElements.define('willtec-complementary-products', WilltecComplementaryProducts);
  }
})();
